import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Bundle, Product } from '@/api/types';
import { ErrorNote, Spinner } from '@/components/Common';
import { UsdCalculator } from '@/components/UsdCalculator';
import { isMeterProduct, isTeloneProduct } from '@/pages/PublicCheckoutPage';

interface VendorSale {
  id: string;
  productId: number;
  productCode: string | null;
  target: string;
  amount: string;
  currency: string;
  customerMsisdn: string | null;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
  orderId: string | null;
  failureReason: string | null;
  paymentMethod: 'ECOCASH' | 'CASH' | 'BANK_TRANSFER';
  createdAt: string;
}

type PaymentMethod = 'CASH' | 'ECOCASH';

const PROVIDER_ORDER = ['Econet', 'NetOne', 'Telecel', 'Electricity', 'Telone', 'Other'];

// Zimbabwe mobile prefixes: 071 NetOne, 073 Telecel, 077/078 Econet.
const MSISDN_RE = /^0(71|73|77|78)\d{7}$/;

function normalizeMsisdn(raw: string): string {
  let v = raw.replace(/[^\d+]/g, '');
  if (v.startsWith('+263')) v = `0${v.slice(4)}`;
  else if (v.startsWith('263')) v = `0${v.slice(3)}`;
  return v;
}

function msisdnError(v: string): string | null {
  if (!v) return null;
  return MSISDN_RE.test(v) ? null : 'Enter a valid number, e.g. 077 292 9223';
}

function groupMsisdn(v: string): string {
  if (v.length < 10) return v;
  return `${v.slice(0, 3)} ${v.slice(3, 6)} ${v.slice(6, 10)}`;
}

const AMOUNT_CHIPS = ['1', '2', '5', '10', '20'];

function providerOf(p: Product): string {
  if (isMeterProduct(p)) return 'Electricity';
  if (isTeloneProduct(p)) return 'Telone';
  const first = (p.network ?? '').split(',')[0]?.trim();
  if (!first || first.toLowerCase() === 'allnetworks') return 'Other';
  return first;
}

/**
 * A vendor's own till: providers first, then that provider's services, then the sale — not one
 * big flat grid of every product at once. Meant to be used dozens of times an hour by someone
 * with a queue in front of them, not once in a while by someone comfortable with a dropdown-heavy
 * admin form.
 */
export function VendorSellPage() {
  const queryClient = useQueryClient();

  const [provider, setProvider] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [active, setActive] = useState<VendorSale | null>(null);

  const { data: catalogueData, isLoading: loadingCatalogue } = useQuery({
    queryKey: ['vendor-products'],
    queryFn: () => api.get<{ products: Product[] }>('/api/v1/vendor/checkout/products'),
  });
  const products = (catalogueData?.products ?? []).filter((p) => p.enabled);
  const selected = products.find((p) => p.productId === selectedId) ?? null;

  const { data: salesData } = useQuery({
    queryKey: ['vendor-sales'],
    queryFn: () => api.get<{ sales: VendorSale[] }>('/api/v1/vendor/checkout'),
    refetchInterval: 10_000,
  });
  const pending = (salesData?.sales ?? []).filter((s) => s.status === 'PENDING' && s.id !== active?.id);

  const confirm = useMutation({
    mutationFn: (id: string) => api.post<VendorSale>(`/api/v1/vendor/checkout/${id}/confirm`),
    onSuccess: (sale) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-sales'] });
      if (active?.id === sale.id) setActive(sale);
    },
  });
  const reject = useMutation({
    mutationFn: (id: string) =>
      api.post<VendorSale>(`/api/v1/vendor/checkout/${id}/reject?reason=${encodeURIComponent('Payment not received')}`),
    onSuccess: (sale) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-sales'] });
      if (active?.id === sale.id) setActive(sale);
    },
  });

  const searching = query.trim().length > 0;
  const searchResults = products.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()));

  const providerCounts = new Map<string, number>();
  products.forEach((p) => {
    const name = providerOf(p);
    providerCounts.set(name, (providerCounts.get(name) ?? 0) + 1);
  });
  const providerRank = (name: string) => {
    const i = PROVIDER_ORDER.indexOf(name);
    return i === -1 ? PROVIDER_ORDER.length - 1 : i; // unlisted providers sort just before "Other"
  };
  const providers = [...providerCounts.keys()].sort(
    (a, b) => providerRank(a) - providerRank(b) || a.localeCompare(b));

  const providerProducts = provider ? products.filter((p) => providerOf(p) === provider) : [];

  function startOver() {
    setActive(null);
    setSelectedId(null);
  }

  function backToProviders() {
    setProvider(null);
    setSelectedId(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-ink-800">Sell</h1>
        {pending.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">
            {pending.length} awaiting payment
          </span>
        )}
      </div>

      {(confirm.error ?? reject.error) && <ErrorNote error={confirm.error ?? reject.error} />}

      {pending.length > 0 && (
        <div className="card divide-y divide-ink-100">
          {pending.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-2 text-sm">
              <div>
                <span className="font-medium text-ink-800">{s.target}</span>
                <span className="ml-2 text-ink-500">{s.currency} {s.amount} · {s.paymentMethod}</span>
              </div>
              <div className="flex gap-2">
                <button type="button" className="btn-secondary text-red-700" disabled={reject.isPending}
                        onClick={() => reject.mutate(s.id)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" disabled={confirm.isPending}
                        onClick={() => confirm.mutate(s.id)}>
                  {s.paymentMethod === 'CASH' ? 'Cash received' : 'Mark paid'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active ? (
        <SaleResult sale={active} onConfirm={() => confirm.mutate(active.id)} onReject={() => reject.mutate(active.id)}
                    confirming={confirm.isPending} rejecting={reject.isPending}
                    error={confirm.error ?? reject.error} onDone={startOver} />
      ) : selected ? (
        <SellForm product={selected} onBack={() => setSelectedId(null)}
                  onSold={(sale) => { setActive(sale); setSelectedId(null); }} />
      ) : (
        <div className="card">
          <div className="p-4">
            <input className="input" value={query} onChange={(e) => { setQuery(e.target.value); setProvider(null); }}
                   placeholder="Search services…" />
          </div>

          {searching ? (
            <ProductGrid products={searchResults} loading={loadingCatalogue} onPick={setSelectedId}
                         empty="Nothing matches that search." />
          ) : provider ? (
            <>
              <div className="flex items-center gap-3 border-t border-ink-100 px-4 py-3">
                <button type="button" className="text-sm font-semibold text-ink-500 hover:text-ink-800"
                        onClick={backToProviders}>
                  ← Back
                </button>
                <h2 className="text-sm font-semibold text-ink-800">{provider}</h2>
              </div>
              <ProductGrid products={providerProducts} loading={loadingCatalogue} onPick={setSelectedId}
                           empty="No services under this provider." bordered={false} />
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3 border-t border-ink-100 p-4 sm:grid-cols-3 lg:grid-cols-4">
              {providers.map((name) => (
                <button key={name} type="button" onClick={() => setProvider(name)}
                        className="flex flex-col gap-1 rounded-lg border border-ink-200 bg-white p-5 text-left hover:border-ink-400 hover:shadow-sm">
                  <span className="text-base font-semibold text-ink-900">{name}</span>
                  <span className="text-xs text-ink-500">
                    {providerCounts.get(name)} service{providerCounts.get(name) === 1 ? '' : 's'}
                  </span>
                </button>
              ))}
              {loadingCatalogue && <div className="col-span-full"><Spinner /></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProductGrid({ products, loading, onPick, empty, bordered = true }: {
  products: Product[];
  loading: boolean;
  onPick: (id: number) => void;
  empty: string;
  bordered?: boolean;
}) {
  return (
    <div className={`grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4 ${bordered ? 'border-t border-ink-100' : ''}`}>
      {products.map((p) => (
        <button key={p.productId} type="button" onClick={() => onPick(p.productId)}
                className="flex flex-col gap-1 rounded-lg border border-ink-200 bg-white p-3 text-left hover:border-ink-400 hover:shadow-sm">
          <span className="text-[11px] font-bold uppercase text-ink-500">{providerOf(p)}</span>
          <span className="text-sm font-semibold text-ink-900">{p.name}</span>
          <span className="text-xs text-ink-500">
            {p.requiresProductCode ? 'Bundle' : 'Top-up'}{p.currency ? ` · ${p.currency}` : ''}
          </span>
        </button>
      ))}
      {loading && <div className="col-span-full"><Spinner /></div>}
      {!loading && products.length === 0 && (
        <p className="col-span-full py-8 text-center text-sm text-ink-500">{empty}</p>
      )}
    </div>
  );
}

function SellForm({ product, onBack, onSold }: {
  product: Product;
  onBack: () => void;
  onSold: (sale: VendorSale) => void;
}) {
  const queryClient = useQueryClient();
  const [productCode, setProductCode] = useState('');
  const [target, setTarget] = useState('');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState(false);
  const [customerMsisdn, setCustomerMsisdn] = useState('');
  const [sameAsTarget, setSameAsTarget] = useState(false);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [touched, setTouched] = useState(false);
  // Bundle products pick from a long list of mini options — that list gets its own step so the
  // recharge-number/payment step isn't buried under dozens of bundle rows.
  const [step, setStep] = useState<'bundle' | 'details'>(product.requiresProductCode ? 'bundle' : 'details');

  const { data: fxData } = useQuery({
    queryKey: ['fx-rate'],
    queryFn: () => api.get<{ usdToZwgRate: number }>('/api/v1/fx-rate'),
    staleTime: 5 * 60_000,
    enabled: product.currency === 'ZWG',
  });

  const { data: bundlesData } = useQuery({
    queryKey: ['vendor-bundles', product.productId],
    queryFn: () => api.get<{ bundles: Bundle[] }>(`/api/v1/vendor/checkout/products/${product.productId}/bundles`),
    enabled: !!product.requiresProductCode,
  });
  const selectedBundle = bundlesData?.bundles.find((b) => b.productCode === productCode) ?? null;

  function pickBundle(code: string) {
    setProductCode(code);
    const bundle = bundlesData?.bundles.find((b) => b.productCode === code);
    if (bundle) setAmount(bundle.value);
    setStep('details');
  }

  const sell = useMutation({
    mutationFn: (m: PaymentMethod) => api.post<VendorSale>('/api/v1/vendor/checkout', {
      productId: product.productId, target, amount,
      productCode: productCode || undefined, paymentMethod: m,
      customerMsisdn: m === 'ECOCASH' ? customerMsisdn : undefined,
    }),
    onSuccess: (sale) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-sales'] });
      onSold(sale);
    },
  });

  const isMobileTarget = !isMeterProduct(product) && !isTeloneProduct(product);
  const targetLabel = isMeterProduct(product) ? 'Meter number'
    : isTeloneProduct(product) ? 'Telone account number' : 'Number to recharge';

  function setTargetValue(raw: string) {
    setTarget(isMobileTarget ? normalizeMsisdn(raw) : raw.replace(/\s+/g, ''));
  }

  function setMsisdnValue(raw: string) {
    setSameAsTarget(false);
    setCustomerMsisdn(normalizeMsisdn(raw));
  }

  const targetError = touched && isMobileTarget ? msisdnError(target) : null;
  const msisdnFieldError = touched && method === 'ECOCASH' ? msisdnError(customerMsisdn) : null;

  const min = product.minAmount ? Number(product.minAmount) : null;
  const max = product.maxAmount ? Number(product.maxAmount) : null;
  const amountNum = Number(amount);
  const amountValid = !!amount && amountNum > 0
    && (min == null || amountNum >= min) && (max == null || amountNum <= max);
  const amountRangeHint = min != null && max != null ? `${min}–${max}` : min != null ? `from ${min}` : max != null ? `up to ${max}` : null;

  const targetValid = !!target && (!isMobileTarget || MSISDN_RE.test(target));
  const detailsReady = targetValid && amountValid && (!product.requiresProductCode || !!productCode);
  const paymentReady = method === 'CASH' ? true : method === 'ECOCASH' ? MSISDN_RE.test(customerMsisdn) : false;
  const canSubmit = detailsReady && method != null && paymentReady && !sell.isPending;

  function submit() {
    setTouched(true);
    if (!canSubmit || !method) return;
    sell.mutate(method);
  }

  if (step === 'bundle') {
    return (
      <div className="card">
        <div className="card-header flex items-center gap-3">
          <button type="button" className="text-sm font-semibold text-ink-500 hover:text-ink-800" onClick={onBack}>
            ← Back
          </button>
          <h2 className="text-sm font-semibold text-ink-800">
            {product.name}{product.network ? ` — ${product.network}` : ''}
          </h2>
        </div>
        <div className="p-4">
          <span className="label">Choose a bundle</span>
          <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {(bundlesData?.bundles ?? []).map((b) => (
              <button key={b.productCode} type="button" onClick={() => pickBundle(b.productCode)}
                      className="min-h-[48px] rounded-lg border border-ink-200 p-2 text-left text-xs hover:border-ink-400">
                <div className="font-semibold">{b.name}</div>
                <div className="text-ink-500">{b.currency} {b.value}</div>
              </button>
            ))}
            {!bundlesData && <Spinner label="Loading bundles" />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header flex items-center gap-3">
        <button type="button" className="text-sm font-semibold text-ink-500 hover:text-ink-800"
                onClick={() => (product.requiresProductCode ? setStep('bundle') : onBack())}>
          ← Back
        </button>
        <h2 className="text-sm font-semibold text-ink-800">
          {product.name}{product.network ? ` — ${product.network}` : ''}
        </h2>
      </div>
      <div className="space-y-4 p-4">
        {product.requiresProductCode && selectedBundle && (
          <div className="flex items-center justify-between rounded-lg bg-ink-50 px-3 py-2 text-sm">
            <div>
              <div className="font-semibold text-ink-900">{selectedBundle.name}</div>
              <div className="text-ink-500">{selectedBundle.currency} {selectedBundle.value}</div>
            </div>
            <button type="button" className="text-xs font-semibold text-ink-600 hover:text-ink-900"
                    onClick={() => setStep('bundle')}>
              Change
            </button>
          </div>
        )}

        <label className="block">
          <span className="label">{targetLabel}</span>
          <input
            className={`input mt-1 h-12 text-base ${targetError ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''}`}
            type="tel" inputMode="numeric" value={target}
            onChange={(e) => setTargetValue(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={isMobileTarget ? '0772929223' : 'e.g. 12345678901'} autoFocus />
          {targetError && <p className="mt-1 text-xs text-red-600">{targetError}</p>}
        </label>

        {!product.requiresProductCode && (
          <div>
            <span className="label">Amount{product.currency ? ` (${product.currency})` : ''}</span>
            {!customAmount ? (
              <div className="mt-1 grid grid-cols-5 gap-2">
                {AMOUNT_CHIPS.map((chip) => (
                  <button key={chip} type="button" onClick={() => setAmount(chip)}
                          className={`h-12 rounded-lg border text-sm font-semibold ${
                            amount === chip && !customAmount
                              ? 'border-ink-900 bg-ink-900 text-white' : 'border-ink-200 hover:border-ink-400'
                          }`}>
                    {chip}
                  </button>
                ))}
                <button type="button" onClick={() => { setCustomAmount(true); setAmount(''); }}
                        className="h-12 rounded-lg border border-ink-200 text-xs font-semibold text-ink-600 hover:border-ink-400">
                  Other
                </button>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <input className="input h-12 text-base tabular" inputMode="decimal" value={amount}
                       onChange={(e) => setAmount(e.target.value)} onBlur={() => setTouched(true)}
                       placeholder="e.g. 5.00" autoFocus />
                <button type="button" onClick={() => { setCustomAmount(false); setAmount(''); }}
                        className="text-xs font-semibold text-ink-500 hover:text-ink-800">
                  Use chips
                </button>
              </div>
            )}
            {touched && !!amount && !amountValid && (
              <p className="mt-1 text-xs text-red-600">
                Enter an amount{amountRangeHint ? ` ${amountRangeHint}` : ' greater than 0'}.
              </p>
            )}
            <UsdCalculator rate={fxData?.usdToZwgRate} currency={product.currency}
                           onApply={(zwg) => { setCustomAmount(true); setAmount(zwg); }} />
          </div>
        )}
      </div>

      {/* Payment method is a selection, not a submit — the Recharge button at the bottom is the
          one and only action that creates the sale. */}
      <div className="space-y-3 border-t border-ink-100 p-4">
        <span className="label">Payment method</span>
        <div className="grid grid-cols-2 gap-2">
          {(['CASH', 'ECOCASH'] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMethod(m)}
                    className={`flex h-12 items-center justify-center gap-1.5 rounded-lg border-2 text-sm font-semibold ${
                      method === m
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                        : 'border-ink-200 bg-white text-ink-700 hover:border-ink-400'
                    }`}>
              {method === m && <span aria-hidden="true">✓</span>}
              {m === 'CASH' ? 'Cash' : 'EcoCash'}
            </button>
          ))}
        </div>

        {method === 'ECOCASH' && (
          <div>
            <div className="flex items-center justify-between">
              <span className="label">EcoCash number</span>
              {isMobileTarget && (
                <label className="flex items-center gap-1.5 text-xs text-ink-600">
                  <input type="checkbox" checked={sameAsTarget}
                         onChange={(e) => {
                           setSameAsTarget(e.target.checked);
                           setCustomerMsisdn(e.target.checked ? target : '');
                         }} />
                  Same as recharge number
                </label>
              )}
            </div>
            <input className={`input mt-1 h-12 text-base ${msisdnFieldError ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''}`}
                   type="tel" inputMode="numeric" value={customerMsisdn}
                   onChange={(e) => setMsisdnValue(e.target.value)} onBlur={() => setTouched(true)}
                   placeholder="0772929223" disabled={sameAsTarget} />
            {msisdnFieldError && <p className="mt-1 text-xs text-red-600">{msisdnFieldError}</p>}
          </div>
        )}
      </div>

      {sell.isError && <div className="px-4"><ErrorNote error={sell.error} /></div>}

      <div className="space-y-2 border-t border-ink-100 p-4">
        {detailsReady && method && (
          <p className="text-center text-sm text-ink-600">
            Recharge {product.currency ?? ''} {amount} to {isMobileTarget ? groupMsisdn(target) : target}
            {' — paid by '}{method === 'CASH' ? 'cash' : 'EcoCash'}
          </p>
        )}
        <button type="button" onClick={submit} disabled={!canSubmit}
                className="h-12 w-full rounded-lg bg-ink-900 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-ink-300">
          {sell.isPending ? 'Processing…'
            : amountValid ? `Recharge ${product.currency ?? ''} ${amount}` : 'Recharge'}
        </button>
      </div>
    </div>
  );
}

function SaleResult({ sale, onConfirm, onReject, confirming, rejecting, error, onDone }: {
  sale: VendorSale;
  onConfirm: () => void;
  onReject: () => void;
  confirming: boolean;
  rejecting: boolean;
  error: unknown;
  onDone: () => void;
}) {
  const tone = sale.status === 'PAID' ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
    : sale.status === 'FAILED' || sale.status === 'EXPIRED' ? 'border-red-300 bg-red-50 text-red-900'
    : 'border-amber-300 bg-amber-50 text-amber-900';

  return (
    <div className={`card border p-5 ${tone}`}>
      {sale.status === 'PENDING' && (
        <>
          <p className="font-semibold">Awaiting payment</p>
          <p className="mt-1 text-sm">
            {sale.target} — {sale.currency} {sale.amount} via {sale.paymentMethod}.
            {sale.paymentMethod === 'CASH'
              ? ' Confirm once you have the cash in hand.'
              : " Confirm once you've verified the EcoCash payment arrived."}
          </p>
          {error != null && <ErrorNote error={error} />}
          <div className="mt-4 flex gap-2">
            <button type="button" className="btn-secondary text-red-700" disabled={rejecting} onClick={onReject}>
              Cancel sale
            </button>
            <button type="button" className="btn-primary" disabled={confirming} onClick={onConfirm}>
              {sale.paymentMethod === 'CASH' ? 'Cash received' : 'Mark paid'}
            </button>
          </div>
        </>
      )}
      {sale.status === 'PAID' && (
        <>
          <p className="font-semibold">Sold — recharge placed</p>
          <p className="mt-1 text-sm">{sale.target} was recharged with {sale.currency} {sale.amount}.</p>
          <button type="button" className="btn-primary mt-4" onClick={onDone}>Sell another</button>
        </>
      )}
      {(sale.status === 'FAILED' || sale.status === 'EXPIRED') && (
        <>
          <p className="font-semibold">Not completed</p>
          <p className="mt-1 text-sm">{sale.failureReason ?? 'This sale was cancelled.'}</p>
          <button type="button" className="btn-primary mt-4" onClick={onDone}>Sell another</button>
        </>
      )}
    </div>
  );
}
